/**
 * 本系统使用四个缓存来管理弹窗的显示逻辑：
 *
 * 1. localStorage.emailPopupSubmitted (永久缓存)
 *    - 作用：标记用户已成功提交邮箱
 *    - 值：'true' 表示已提交
 *    - 写入时机：用户成功提交邮箱表单时
 *    - 清除时机：永不清除（除非用户手动清除浏览器缓存）
 *    - 优先级：最高（一旦设置，永久不显示任何弹窗）
 *
 * 2. localStorage.emailPopupReminderState (永久缓存)
 *    - 作用：标记小提示弹窗的显示状态
 *    - 值：'active' 表示应该显示小提示弹窗
 *    - 写入时机：小提示弹窗显示时（仅在桌面端）
 *    - 清除时机：用户成功提交邮箱时
 *    - 用途：页面刷新后继续显示小提示弹窗，直到用户提交邮箱
 *
 * 3. sessionStorage.emailPopupReminderDismissed (会话缓存)
 *    - 作用：标记用户在本次会话中主动关闭了小提示弹窗
 *    - 值：'true' 表示已关闭
 *    - 写入时机：用户点击小提示弹窗的关闭按钮 (×) 时
 *    - 清除时机：用户成功提交邮箱时，或关闭浏览器标签页时自动清除
 *    - 用途：本次会话中不再显示任何弹窗，表示用户明确拒绝
 *
 * 4. sessionStorage.emailPopupMobileDismissed (会话缓存)
 *    - 作用：标记移动端用户在本次会话中关闭了邮箱弹窗
 *    - 值：'true' 表示已关闭
 *    - 写入时机：移动端（≤750px）用户关闭邮箱弹窗时
 *    - 清除时机：关闭浏览器标签页时自动清除
 *    - 用途：移动端关闭后本次会话不再显示邮箱弹窗和小提示弹窗
 *
 */

class EmailPopupModal {
  constructor() {
    this.modal = document.getElementById("emailPopupModal");
    if (!this.modal) return;

    this.overlay = this.modal.querySelector(".email-popup-overlay");
    this.closeBtn = this.modal.querySelector(".email-popup-close");
    this.form = this.modal.querySelector(".newsletter-form");

    // Get settings from data attributes
    this.delay = parseInt(this.modal.dataset.delay) * 1000 || 5000;
    this.successTitle = this.modal.dataset.successTitle || "SUCCESS !";
    this.successMessage =
      this.modal.dataset.successMessage ||
      "Copy the discount code and apply it during checkout to enjoy a discount on all our products.";
    this.discountLabel = this.modal.dataset.discountLabel || "Discount code:";
    this.discountCode = this.modal.dataset.discountCode || "--";
    this.copySuccessText = this.modal.dataset.copySuccess || "copied!";
    this.errorInvalidEmail =
      this.modal.dataset.errorInvalidEmail ||
      "Please enter a valid email address";
    this.submittingText = this.modal.dataset.submittingText || "Submitting...";
    this.reminderTitle =
      this.modal.dataset.reminderTitle || "Get your first-order discount!";
    this.reminderButtonText = this.modal.dataset.reminderButton || "Get it";

    // 小提示弹窗相关元素
    this.reminder = document.getElementById("emailPopupReminder");
    this.reminderCloseBtn = this.reminder?.querySelector(
      ".email-popup-reminder-close"
    );
    this.reminderGetItBtn = document.getElementById("emailPopupReminderBtn");

    // 标记用户是否已经提交过邮箱
    this.hasSubmitted = false;

    this.init();
  }

  init() {
    // Set up event listeners
    this.setupEventListeners();

    // 检查用户是否已经成功提交过邮箱
    const hasSubmittedBefore = localStorage.getItem("emailPopupSubmitted");
    if (hasSubmittedBefore === "true") {
      // 如果已经提交过，永久不再显示任何弹窗
      return;
    }

    // 检查移动端用户是否在本次会话中关闭过邮箱弹窗
    const mobileDismissed = sessionStorage.getItem("emailPopupMobileDismissed");
    if (mobileDismissed === "true") {
      // 如果移动端用户已关闭过，本次会话不再显示任何弹窗
      return;
    }

    // 检查用户是否点击过关闭小提示弹窗的按钮
    const reminderDismissed = sessionStorage.getItem(
      "emailPopupReminderDismissed"
    );

    // 如果用户已经关闭过小提示弹窗，则不显示任何弹窗
    if (reminderDismissed) {
      return;
    }

    // 检查是否有小提示弹窗状态的缓存
    const reminderState = localStorage.getItem("emailPopupReminderState");
    if (reminderState === "active") {
      // 如果有缓存，直接显示小提示弹窗
      this.showReminder();
    } else {
      // 否则按正常流程显示邮箱弹窗
      this.showWithDelay();
    }
  }

  setupEventListeners() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener("click", () => this.closePopup());
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener("click", () => this.closePopup());
    }

    // Escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.modal.classList.contains("active")) {
        this.closePopup();
      }
    });

    // Submit button click - 直接监听按钮点击而不是表单提交
    const submitBtn = this.modal.querySelector("#emailPopupSubmitBtn");
    if (submitBtn) {
      submitBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleFormSubmit(e);
      });
      console.log("Email popup submit button listener attached");
    }

    // 支持回车键提交
    const emailInput = this.modal.querySelector("#EmailPopupForm");
    if (emailInput) {
      emailInput.addEventListener("keypress", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          this.handleFormSubmit(e);
        }
      });
    }

    // 额外的表单提交拦截（双重保险）
    if (this.form) {
      this.form.addEventListener(
        "submit",
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        },
        true
      );
    }

    // Prevent clicks inside modal from closing it
    const container = this.modal.querySelector(".email-popup-container");
    if (container) {
      container.addEventListener("click", (e) => e.stopPropagation());
    }

    // 小提示弹窗事件监听器
    if (this.reminderCloseBtn) {
      this.reminderCloseBtn.addEventListener(
        "click",
        () => this.closeReminder(true) // 传入 true 表示用户主动关闭
      );
    }

    if (this.reminderGetItBtn) {
      this.reminderGetItBtn.addEventListener("click", () => {
        this.closeReminder(false); // 传入 false 表示不是主动关闭
        // 重置错误信息
        this.clearError();
        this.showPopup();
      });
    }
  }

  showWithDelay() {
    setTimeout(() => {
      this.showPopup();
    }, this.delay);
  }

  showPopup() {
    this.modal.classList.add("active");
    document.body.style.overflow = "hidden";

    // Focus on the email input
    const emailInput = this.modal.querySelector("#EmailPopupForm");
    if (emailInput) {
      setTimeout(() => emailInput.focus(), 300);
    }

    // Track popup view (optional - for analytics)
    this.trackEvent("popup_shown");
  }

  closePopup() {
    // 检查是否是移动端（宽度 <= 750px）
    const isMobile = window.innerWidth <= 750;

    // Add closing animation class
    this.modal.classList.add("closing");

    // Remove classes after animation
    setTimeout(() => {
      this.modal.classList.remove("active", "closing");
      document.body.style.overflow = "";

      // 移动端逻辑：写入 session 缓存，不显示小提示弹窗
      if (isMobile) {
        sessionStorage.setItem("emailPopupMobileDismissed", "true");
        // 移动端直接返回，不显示小提示弹窗
        return;
      }

      // 桌面端逻辑：检查用户是否点击过关闭小提示弹窗的按钮
      const reminderDismissed = sessionStorage.getItem(
        "emailPopupReminderDismissed"
      );

      // 只有在用户没有提交过邮箱且没有关闭过小提示弹窗的情况下，才显示小提示弹窗
      if (!this.hasSubmitted && !reminderDismissed) {
        this.showReminder();
      }
    }, 300);

    // Track popup close (optional - for analytics)
    this.trackEvent("popup_closed");
  }

  async handleFormSubmit(e) {
    console.log("Form submit handler called");

    // Get form data
    const formData = new FormData(this.form);
    const email = formData.get("contact[email]");

    console.log("Email submitted:", email);

    // Basic validation
    if (!email || !email.includes("@")) {
      this.showError(this.errorInvalidEmail);
      return;
    }

    // Disable submit button
    const submitBtn = this.modal.querySelector("#emailPopupSubmitBtn");
    if (!submitBtn) {
      console.error("Submit button not found");
      return;
    }
    const originalText = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.textContent = this.submittingText;

    // 【附属功能】静默提交到 Shopify 订阅，不影响主流程
    try {
      // 创建隐藏的 iframe 用于提交表单
      let iframe = document.getElementById("newsletter-submit-iframe");
      if (!iframe) {
        iframe = document.createElement("iframe");
        iframe.id = "newsletter-submit-iframe";
        iframe.name = "newsletter-submit-iframe";
        iframe.style.display = "none";
        document.body.appendChild(iframe);
      }

      // 克隆整个表单（保留 Shopify 自动生成的所有隐藏字段，包括 hCaptcha）
      const formClone = this.form.cloneNode(true);
      formClone.target = "newsletter-submit-iframe";
      formClone.style.display = "none";

      // 移除克隆表单中的按钮事件监听（避免重复触发）
      const cloneBtn = formClone.querySelector("#emailPopupSubmitBtn");
      if (cloneBtn) {
        cloneBtn.removeAttribute("id");
      }

      document.body.appendChild(formClone);

      // 提交克隆的表单到 iframe（Shopify 会自动处理所有验证）
      formClone.submit();

      // 清理克隆的表单
      setTimeout(() => {
        if (formClone.parentNode) {
          document.body.removeChild(formClone);
        }
      }, 1500);

      console.log("Newsletter subscription submitted silently to Shopify");
    } catch (error) {
      console.log("Newsletter subscription failed (non-critical):", error);
      // 不影响主流程，继续执行
    }

    // 【主要功能】延迟后显示折扣码
    setTimeout(() => {
      // 标记用户已提交
      this.hasSubmitted = true;

      // 清除所有相关缓存
      localStorage.removeItem("emailPopupReminderState");
      sessionStorage.removeItem("emailPopupReminderDismissed");
      sessionStorage.removeItem("emailPopupMobileDismissed");

      // 写入提交成功的缓存，永久标记用户已提交
      localStorage.setItem("emailPopupSubmitted", "true");

      // Track form submission
      this.trackEvent("popup_form_submitted");

      // 显示折扣码成功页面
      this.showSuccess();
    }, 1000);
  }

  showSuccess() {
    // Hide form content
    const formWrapper = this.modal.querySelector(".email-popup-form-wrapper");
    if (formWrapper) {
      // Create and insert success screen
      const successHTML = `
        <div class="email-popup-success-screen" id="EmailPopup-success">
          <h2 class="email-popup-success-title">${this.successTitle.replace(
            /\n/g,
            "<br>"
          )}</h2>
          <div class="email-popup-success-message">
            <p>${this.successMessage
              .replace(/\n/g, "<br>")
              .replace(/\{DISCOUNT_CODE\}/g, `${this.discountCode}`)}</p>
          </div>
          <div class="email-popup-discount-section">
            <p class="email-popup-discount-label">${this.discountLabel}</p>
            <div class="email-popup-discount-code" id="discountCodeElement" title="点击复制">${
              this.discountCode
            }</div>
            <div class="email-popup-copy-success" id="copySuccessMessage">${
              this.copySuccessText
            }</div>
          </div>
        </div>
      `;
      formWrapper.insertAdjacentHTML("beforeend", successHTML);

      // 添加点击复制功能
      const discountCodeElement = document.getElementById(
        "discountCodeElement"
      );
      if (discountCodeElement) {
        discountCodeElement.addEventListener("click", () =>
          this.copyDiscountCode()
        );
      }
    }
  }

  copyDiscountCode() {
    const discountCodeElement = document.getElementById("discountCodeElement");
    const copySuccessMessage = document.getElementById("copySuccessMessage");

    if (!discountCodeElement) return;

    // 复制到剪贴板
    const textToCopy = this.discountCode;

    // 使用现代 Clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          this.showCopySuccess(copySuccessMessage);
        })
        .catch((err) => {
          console.error("复制失败:", err);
          // 降级到传统方法
          this.fallbackCopyTextToClipboard(textToCopy, copySuccessMessage);
        });
    } else {
      // 降级到传统方法
      this.fallbackCopyTextToClipboard(textToCopy, copySuccessMessage);
    }
  }

  fallbackCopyTextToClipboard(text, successElement) {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.top = "-9999px";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        this.showCopySuccess(successElement);
      }
    } catch (err) {
      console.error("降级复制方法也失败了:", err);
    }

    document.body.removeChild(textArea);
  }

  showCopySuccess(element) {
    if (!element) return;

    // 显示复制成功消息
    element.classList.add("show");

    // 2秒后隐藏
    setTimeout(() => {
      element.classList.remove("show");
    }, 2000);
  }

  showError(message) {
    // Remove existing error messages
    const existingError = this.modal.querySelector(".email-popup-error");
    if (existingError) {
      existingError.remove();
    }

    // Create error message
    const errorHTML = `
      <small class="email-popup-message email-popup-error">
        ${message}
      </small>
    `;

    const fieldWrapper = this.modal.querySelector(".email-popup-field-wrapper");
    if (fieldWrapper) {
      fieldWrapper.insertAdjacentHTML("beforeend", errorHTML);
    }
  }

  clearError() {
    // Remove existing error messages
    const existingError = this.modal?.querySelector(".email-popup-error");
    if (existingError) {
      existingError.remove();
    }
  }

  trackEvent(eventName) {
    // Analytics tracking - customize based on your analytics setup
    if (typeof gtag !== "undefined") {
      gtag("event", eventName, {
        event_category: "Email Popup",
        event_label: "Newsletter Signup",
      });
    }

    // Shopify Analytics
    if (typeof window.ShopifyAnalytics !== "undefined") {
      window.ShopifyAnalytics.lib.track(eventName, {
        category: "Email Popup",
      });
    }

    // Console log for debugging
    console.log("Email Popup Event:", eventName);
  }

  // 显示小提示弹窗
  showReminder() {
    if (!this.reminder) return;

    // 延迟0.5秒后显示提示弹窗
    setTimeout(() => {
      this.reminder.classList.add("active");
      // 写入缓存，标记小提示弹窗状态
      localStorage.setItem("emailPopupReminderState", "active");
      this.trackEvent("reminder_shown");
    }, 500);
  }

  // 关闭小提示弹窗
  closeReminder(isDismissed = false) {
    if (!this.reminder) return;

    this.reminder.classList.add("closing");

    setTimeout(() => {
      this.reminder.classList.remove("active", "closing");

      // 只有在用户主动点击关闭按钮时，才写入 sessionStorage 缓存
      // localStorage 保持不变，只在提交邮箱成功时才清除
      if (isDismissed) {
        sessionStorage.setItem("emailPopupReminderDismissed", "true");
      }
    }, 300);

    this.trackEvent("reminder_closed");
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new EmailPopupModal();
  });
} else {
  new EmailPopupModal();
}

// Also initialize on Shopify section load (for theme editor)
if (typeof Shopify !== "undefined" && Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) => {
    if (event.detail.sectionId.includes("email-popup-modal")) {
      new EmailPopupModal();
    }
  });
}
